/**
 * WBS Structure — İş Kırınımları
 * ================================
 * Şantiye bazlı bağımsız iş kalemi ağacı. Sonsuz iç içe hiyerarşi.
 * Kategoriler PROJE bazlıdır (site_id ile bağlı), globalde paylaşılmaz.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  TreeStructure, Plus, PencilSimple, TrashSimple, ArrowLeft,
  FileXls, DownloadSimple, UploadSimple, FilePdf,
} from "@phosphor-icons/react";

// (Faz 29 sonrası: Metraj artık WBS'de değil, Work Item'da tutulur.)


const canManageRoles = new Set(["yonetici"]);

export default function WBSStructure() {
  const { siteId } = useParams();
  const { user } = useAuth();
  const askConfirm = useConfirm();
  const canManage = !!user;

  const [site, setSite] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState({ id: null, name: "", category: "", parent_id: null });

  // Excel import state
  const [impOpen, setImpOpen] = useState(false);
  const [impFile, setImpFile] = useState(null);
  const [impBusy, setImpBusy] = useState(false);
  const [impResult, setImpResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [sr, nr, cr] = await Promise.all([
        axios.get(`${API}/sites/${siteId}/project`),
        axios.get(`${API}/sites/${siteId}/wbs-nodes`),
        axios.get(`${API}/sites/${siteId}/wbs-categories`),
      ]);
      setSite({ id: siteId, name: sr.data.site_name, code: sr.data.site_code });
      setNodes(nr.data || []);
      setCategories(cr.data || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [siteId]);

  // --- Tree ---
  const tree = useMemo(() => {
    const byParent = new Map();
    for (const n of nodes) {
      const pid = n.parent_id || null;
      if (!byParent.has(pid)) byParent.set(pid, []);
      byParent.get(pid).push(n);
    }
    for (const arr of byParent.values()) {
      arr.sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    return byParent;
  }, [nodes]);

  const rows = useMemo(() => {
    const out = [];
    const walk = (list, depth) => {
      for (const n of list) {
        const kids = tree.get(n.id) || [];
        out.push({ node: n, depth, childCount: kids.length });
        walk(kids, depth + 1);
      }
    };
    walk(tree.get(null) || [], 0);
    return out;
  }, [tree]);

  // Kategori bazlı sayaçlar (üst şerit)
  const categoryCounts = useMemo(() => {
    const counts = new Map();
    for (const n of nodes) {
      const cat = (n.category || "").trim() || "Diğer";
      counts.set(cat, (counts.get(cat) || 0) + 1);
    }
    const order = [];
    const seen = new Set();
    for (const r of rows) {
      const cat = (r.node.category || "").trim() || "Diğer";
      if (!seen.has(cat)) { seen.add(cat); order.push(cat); }
    }
    return order.map((cat) => ({ category: cat, count: counts.get(cat) || 0 }));
  }, [nodes, rows]);

  // --- Actions ---
  const openNew = (parent = null) => {
    const parent_id = parent ? parent.id : null;
    const siblings = nodes.filter((x) => (x.parent_id || null) === parent_id);
    setForm({
      id: null, name: "", category: "",
      parent_id, order: siblings.length + 1,
    });
    setDlgOpen(true);
  };
  const openEdit = (n) => {
    setForm({
      id: n.id, name: n.name, category: n.category || "",
      parent_id: n.parent_id || null, order: n.order || 0,
    });
    setDlgOpen(true);
  };
  const save = async () => {
    const name = form.name.trim();
    const category = (form.category || "").trim();
    if (!name) { toast.error("Ad alanı zorunlu"); return; }
    if (!category) { toast.error("Kategori zorunlu"); return; }
    try {
      // Yeni kategori ise proje bazlı listeye kaydet
      const exists = categories.some(
        (c) => (c.name || "").toLowerCase() === category.toLowerCase());
      if (!exists) {
        try {
          const { data: nc } = await axios.post(
            `${API}/sites/${siteId}/wbs-categories`, { name: category });
          setCategories((prev) => {
            if (prev.some((c) => c.id === nc.id)) return prev;
            return [...prev, nc].sort((a, b) => a.name.localeCompare(b.name, "tr"));
          });
        } catch (_) { /* sessizce geç */ }
      }
      const payload = {
        name, category,
        parent_id: form.parent_id || null,
        order: parseInt(form.order) || 0,
        notes: "",
      };
      if (form.id) {
        await axios.put(`${API}/wbs-nodes/${form.id}`, payload);
        toast.success("Güncellendi");
      } else {
        await axios.post(`${API}/sites/${siteId}/wbs-nodes`, payload);
        toast.success("Eklendi");
      }
      setDlgOpen(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (n) => {
    const countDesc = (id) => {
      const kids = nodes.filter((x) => x.parent_id === id);
      return kids.reduce((s, k) => s + 1 + countDesc(k.id), 0);
    };
    const desc = countDesc(n.id);
    const descNote = desc > 0 ? ` Ayrıca ${desc} alt kalem de birlikte silinecektir.` : "";
    const ok = await askConfirm({
      title: "Kalemi Sil",
      description: `"${n.name}" (${n.category || "-"}) kalemini silmek istiyor musunuz?${descNote}`,
      destructive: true, confirmText: "Sil",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/wbs-nodes/${n.id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // ---- Excel şablon indirme ----
  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/sites/${siteId}/wbs-nodes/template.xlsx`,
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "wbs_sablonu.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };
  // ---- İçe aktarım ----
  const doImport = async () => {
    if (!impFile) { toast.error("Excel dosyası seçin"); return; }
    setImpBusy(true);
    setImpResult(null);
    try {
      const fd = new FormData();
      fd.append("file", impFile);
      const { data } = await axios.post(
        `${API}/sites/${siteId}/wbs-nodes/import`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setImpResult(data);
      if (data.inserted > 0) toast.success(`${data.inserted} kalem aktarıldı`);
      if (data.errors?.length) toast.warning(`${data.errors.length} satır hatalı`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setImpBusy(false); }
  };
  const closeImport = () => {
    setImpOpen(false);
    setImpFile(null);
    setImpResult(null);
  };

  return (
    <Shell
      title="WBS Kırınımları"
      subtitle="Proje Yönetimi"
      hideSiteSelector
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => window.open(`${API}/sites/${siteId}/wbs-nodes/export.pdf`, "_blank")}
                  data-testid="wbs-export-pdf"
                  className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest h-9">
            <FilePdf size={14} className="mr-1.5 text-red-600" /> Liste PDF
          </Button>
          <Button variant="outline" onClick={() => window.open(`${API}/sites/${siteId}/wbs-nodes/tree-export.pdf`, "_blank")}
                  data-testid="wbs-export-tree-pdf"
                  className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest h-9">
            <TreeStructure size={14} className="mr-1.5 text-[#1B4776]" /> Görsel Ağaç PDF
          </Button>
          <Link to="/proje/wbs">
            <Button variant="outline" className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest h-9"
                    data-testid="wbs-back">
              <ArrowLeft size={14} className="mr-1.5" /> Şantiye Listesi
            </Button>
          </Link>
        </div>
      }
    >
      {/* Üst şerit — dinamik kategori sayaçları */}
      {site && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="wbs-stats">
          <Card className="p-4 rounded-none sm:rounded-sm border-l-4 border-[#95C11F]">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Proje</div>
            <div className="text-lg font-bold text-slate-900 mt-1 truncate">{site.name}</div>
            <div className="text-xs text-slate-500 font-mono">{site.code}</div>
          </Card>
          {categoryCounts.length === 0 ? (
            <Card className="p-4 rounded-none sm:rounded-sm md:col-span-3 flex items-center justify-center text-xs text-slate-400 italic">
              Henüz iş kalemi eklenmedi — kategori bazlı sayaçlar burada görünecek
            </Card>
          ) : (
            categoryCounts.map((c, idx) => (
              <Card key={c.category}
                    data-testid={`wbs-stat-cat-${c.category}`}
                    className="p-4 rounded-none sm:rounded-sm">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 truncate">
                  Toplam {c.category}
                </div>
                <div className={`text-3xl font-bold mt-1 font-heading ${idx === 0 ? "text-[#1B4776]" : "text-[#95C11F]"}`}>
                  {c.count}
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-sm text-slate-500">Yükleniyor…</div>
      ) : nodes.length === 0 ? (
        <Card className="rounded-none sm:rounded-sm border-2 border-dashed border-slate-300 bg-slate-50/50 py-16 px-6 text-center" data-testid="wbs-empty">
          <div className="mx-auto w-16 h-16 rounded-full bg-white flex items-center justify-center border border-slate-200 mb-4">
            <TreeStructure size={32} weight="duotone" className="text-[#95C11F]" />
          </div>
          <div className="text-lg font-bold text-slate-900">
            Henüz WBS oluşturulmadı
          </div>
          <div className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
            İş Kırınım Yapınızı manuel olarak inşa edin. Kategori serbesttir —
            örn: İnce İmalatlar › A-Blok › Poz-01, Poz-02…
          </div>
          {canManage && (
            <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
              <Button onClick={() => openNew(null)} data-testid="wbs-create-btn"
                      className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white h-11 px-6">
                <Plus size={16} className="mr-2" />
                <span className="text-xs uppercase tracking-[0.2em] font-bold">
                  Manuel Oluştur
                </span>
              </Button>
              <Button onClick={() => setImpOpen(true)} data-testid="wbs-import-btn-empty"
                      variant="outline"
                      className="rounded-none sm:rounded-sm h-11 px-6 border-emerald-700 text-emerald-700 hover:bg-emerald-50">
                <FileXls size={16} className="mr-2" />
                <span className="text-xs uppercase tracking-[0.2em] font-bold">
                  Excel'den İçe Aktar
                </span>
              </Button>
            </div>
          )}
        </Card>
      ) : (
        <>
          {/* Hiyerarşik liste */}
          <Card className="rounded-none sm:rounded-sm border-slate-200" data-testid="wbs-tree">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700 flex items-center gap-2">
                <TreeStructure size={16} weight="duotone" className="text-[#95C11F]" />
                WBS Hiyerarşisi
              </div>
              {canManage && (
                <div className="flex gap-2">
                  <Button onClick={() => setImpOpen(true)} data-testid="wbs-import-btn"
                          size="sm" variant="outline"
                          className="h-8 rounded-none sm:rounded-sm">
                    <FileXls size={12} className="mr-1 text-emerald-700" />
                    <span className="text-[10px] uppercase tracking-widest font-bold">Excel İçe Aktar</span>
                  </Button>
                  <Button onClick={() => openNew(null)} data-testid="wbs-new-btn"
                          size="sm" className="h-8 rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
                    <Plus size={12} className="mr-1" />
                    <span className="text-[10px] uppercase tracking-widest font-bold">Ana Kalem Ekle</span>
                  </Button>
                </div>
              )}
            </div>
            <div className="p-2">
              {rows.map((r) => (
                <WBSRow
                  key={r.node.id}
                  node={r.node}
                  childCount={r.childCount}
                  depth={r.depth}
                  canManage={canManage}
                  onAddChild={openNew}
                  onEdit={openEdit}
                  onDelete={del}
                />
              ))}
            </div>
          </Card>

          {/* Görsel Ağaç */}
          <Card className="rounded-none sm:rounded-sm border-slate-200 mt-6" data-testid="wbs-chart">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700 flex items-center gap-2">
                <TreeStructure size={16} weight="duotone" className="text-[#1B4776]" />
                Görsel Ağaç
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">
                Yatay kaydırılabilir · Yakınlaştırma butonları veya CTRL + fare tekerleği
              </div>
            </div>
            <div className="p-6 overflow-x-auto bg-slate-50/50">
              <WBSChart tree={tree} projectLabel={site?.name || "Proje"} projectCode={site?.code || ""} />
            </div>
          </Card>
        </>
      )}

      {/* Dialog */}
      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="rounded-none sm:rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-bold tracking-tight font-heading">
              {form.id ? "İş Kalemini Düzenle" : "Yeni İş Kalemi"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              Kategoriyi ve adı serbestçe belirleyin. Kategoriler bu proje kapsamında hatırlanır.
            </DialogDescription>
          </DialogHeader>
          <datalist id="wbs-category-suggestions">
            {categories.map((c) => (
              <option key={c.id} value={c.name} />
            ))}
          </datalist>
          <div className="space-y-4">
            {form.parent_id && (() => {
              const parent = nodes.find((x) => x.id === form.parent_id);
              return parent ? (
                <div className="bg-slate-50 border border-slate-200 px-3 py-2 rounded-sm flex items-center gap-2"
                     data-testid="wbs-form-parent-banner">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-[#95C11F]/15 text-[9px] uppercase tracking-widest font-bold text-[#5a7a10]">
                    {parent.category || "-"}
                  </span>
                  <span className="text-xs text-slate-500">Alt kalem olarak:</span>
                  <span className="text-sm font-bold text-slate-900 truncate">{parent.name}</span>
                </div>
              ) : null;
            })()}
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Kategori</Label>
              <Input value={form.category}
                     data-testid="wbs-form-category"
                     list="wbs-category-suggestions"
                     onChange={(e) => setForm({ ...form, category: e.target.value })}
                     placeholder="Örn: İnce İmalat / Blok / Poz"
                     className="mt-2 rounded-none sm:rounded-sm" />
              <div className="text-[10px] text-slate-500 mt-1">
                Listeden seçin veya kendi kategorinizi yazın — bu projede tekrar kullanılır.
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Adı / Kodu</Label>
              <Input value={form.name}
                     data-testid="wbs-form-name"
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="Örn: İnce İmalatlar, A-Blok, Poz-01 Duvar Örülmesi"
                     className="mt-2 rounded-none sm:rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)}
                    className="rounded-none sm:rounded-sm">İptal</Button>
            <Button onClick={save} data-testid="wbs-form-save"
                    className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
              {form.id ? "Güncelle" : "Ekle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={impOpen} onOpenChange={(v) => v ? setImpOpen(true) : closeImport()}>
        <DialogContent className="rounded-none sm:rounded-sm max-w-xl" data-testid="wbs-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold font-heading flex items-center gap-2">
              <FileXls size={18} className="text-emerald-700" />
              Excel'den WBS İçe Aktar
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              Hiyerarşi <b>WBS Kodu</b> ile temsil edilir (örn: 1 · 1.1 · 1.1.1).
              Her kod için üst kodun (parent) satırı da olmalıdır.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-slate-50 border border-slate-200 p-3 rounded-sm text-xs text-slate-700">
              <div className="font-bold text-slate-900 mb-1">Kolonlar:</div>
              <div className="grid grid-cols-4 gap-2 text-[11px]">
                <div><b>Kod</b><br/><span className="text-slate-500">1 · 1.1 · 1.1.1</span></div>
                <div><b>Kategori</b><br/><span className="text-slate-500">Ana Grup / Blok / Poz</span></div>
                <div><b>Ad</b><br/><span className="text-slate-500">İnce İmalatlar…</span></div>
                <div><b>Notlar</b><br/><span className="text-slate-500">Opsiyonel</span></div>
              </div>
            </div>
            <Button variant="outline" onClick={downloadTemplate}
                    data-testid="wbs-tpl-download"
                    className="rounded-none sm:rounded-sm h-9 w-full">
              <DownloadSimple size={14} className="mr-1.5" />
              <span className="text-xs uppercase tracking-widest font-bold">Excel Şablonu İndir</span>
            </Button>
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-700">
                Doldurulmuş Excel Dosyası (.xlsx) *
              </Label>
              <Input type="file" accept=".xlsx,.xlsm"
                     onChange={(e) => { setImpFile(e.target.files?.[0] || null); setImpResult(null); }}
                     data-testid="wbs-imp-file"
                     className="rounded-none sm:rounded-sm mt-1" />
              {impFile && (
                <div className="text-[11px] text-slate-600 mt-1 font-mono truncate">
                  {impFile.name} · {(impFile.size / 1024).toFixed(1)} KB
                </div>
              )}
            </div>

            {impResult && (
              <div className="border rounded-sm p-3 space-y-1"
                   style={{
                     borderColor: impResult.errors?.length ? "#F59E0B" : "#059669",
                     background: impResult.errors?.length ? "#FEF3C7" : "#D1FAE5",
                   }}
                   data-testid="wbs-imp-result">
                <div className="text-xs font-bold text-slate-900">
                  Sonuç: {impResult.inserted} kalem eklendi
                  {impResult.errors?.length ? ` · ${impResult.errors.length} hata` : ""}
                  <span className="text-slate-500 font-normal ml-1">
                    (Toplam {impResult.total_rows} satır)
                  </span>
                </div>
                {impResult.errors?.length > 0 && (
                  <ul className="text-[11px] text-slate-700 max-h-32 overflow-auto space-y-0.5 mt-1 font-mono">
                    {impResult.errors.slice(0, 30).map((err, i) => (
                      <li key={i}>
                        <b>Satır {err.row}:</b> {err.error}
                      </li>
                    ))}
                    {impResult.errors.length > 30 && (
                      <li className="italic text-slate-500">…{impResult.errors.length - 30} hata daha</li>
                    )}
                  </ul>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeImport}
                    className="rounded-none sm:rounded-sm">Kapat</Button>
            <Button onClick={doImport} disabled={impBusy || !impFile}
                    data-testid="wbs-imp-submit"
                    className="rounded-none sm:rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white">
              <UploadSimple size={14} className="mr-1.5" />
              <span className="text-xs uppercase tracking-widest font-bold">
                {impBusy ? "Yükleniyor…" : "İçe Aktar"}
              </span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
function WBSRow({ node, childCount = 0, depth, canManage, onAddChild, onEdit, onDelete }) {
  const hasChildren = childCount > 0;
  const indentPx = depth * 20;
  return (
    <div className="select-none" data-testid={`wbs-node-${node.id}`}>
      <div className="flex items-center gap-2 px-2 py-2 rounded-sm hover:bg-slate-50 group relative"
           style={{ paddingLeft: 8 + indentPx }}>
        {depth > 0 && (
          <span className="absolute left-0 top-0 bottom-0 border-l border-slate-200"
                style={{ left: 8 + (depth - 1) * 20 + 10 }} aria-hidden />
        )}
        <TreeStructure size={16} weight="duotone"
                        className={depth === 0 ? "text-[#1B4776] flex-shrink-0" : "text-[#95C11F] flex-shrink-0"} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-[#1B4776]/10 text-[9px] uppercase tracking-widest font-bold text-[#1B4776]">
              {node.category || "-"}
            </span>
            <span className="font-bold text-slate-900 truncate">{node.name}</span>
            {hasChildren && (
              <span className="text-[9px] uppercase tracking-widest font-bold text-slate-500">
                {childCount} alt bölüm
              </span>
            )}
          </div>
        </div>
        {canManage && (
          <div className="flex gap-1 flex-shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
            <button onClick={() => onAddChild(node)}
                    data-testid={`wbs-add-child-${node.id}`}
                    title="Alt Bölüm Ekle"
                    className="h-7 px-2 flex items-center gap-1 border border-[#95C11F] bg-[#95C11F] hover:bg-[#7ea11a] text-[#1B2A3A] text-[10px] uppercase tracking-widest font-bold">
              <Plus size={11} /> Alt Bölümler
            </button>
            <button onClick={() => onEdit(node)}
                    data-testid={`wbs-edit-${node.id}`}
                    title="Kalemi Düzenle"
                    className="w-7 h-7 flex items-center justify-center text-[#1B4776] hover:bg-slate-100">
              <PencilSimple size={12} />
            </button>
            <button onClick={() => onDelete(node)}
                    data-testid={`wbs-del-${node.id}`}
                    title="Kalemi Sil"
                    className="w-7 h-7 flex items-center justify-center text-red-600 hover:bg-red-50">
              <TrashSimple size={12} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// ---- WBSChart — Görsel organizasyon şeması ----
// visual-edits babel plugin'i self-referencing komponentlerde patlıyor;
// bu nedenle iç recursion (renderNode) tercih edildi.
function WBSChart({ tree, projectLabel, projectCode }) {
  // Zoom seviyesi — 25/50/75/100/125% hızlı butonları
  const [zoom, setZoom] = React.useState(1.0);
  const ZOOM_STEPS = [0.25, 0.5, 0.75, 1.0, 1.25];
  // Kategori bazlı deterministik renk atama: aynı kategori → aynı renk
  const PALETTE = [
    "#1B4776", "#2563eb", "#95C11F", "#B45309",
    "#8b5cf6", "#0891b2", "#dc2626", "#16a34a",
    "#db2777", "#f59e0b", "#4f46e5", "#0d9488",
  ];
  const catColor = (cat) => {
    let h = 0;
    for (const ch of (cat || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return PALETTE[h % PALETTE.length];
  };
  const renderNode = (node) => {
    const children = tree.get(node.id) || [];
    const hasChildren = children.length > 0;
    const accent = catColor(node.category);
    // Kural: sadece tüm çocuklar YAPRAK (leaf) ise dikey 2-sütun layout.
    // Branch'lerde yatay layout — okunurluk için (deep tree'lerde daha iyi).
    const childrenAreAllLeaves = hasChildren && children.every(
      (c) => (tree.get(c.id) || []).length === 0
    );
    const useVertical = childrenAreAllLeaves;
    const ulCls = "wc-children" + (useVertical ? " wc-children-vertical" : "");
    return (
      <li className="wc-node" key={node.id} data-testid={`wbs-chart-node-${node.id}`}>
        <div className="wc-box" style={{ borderTopColor: accent }}>
          <div className="wc-cat" style={{ backgroundColor: accent }}>{node.category || "-"}</div>
          <div className="wc-name" title={node.name}>{node.name}</div>
          {hasChildren && <div className="wc-count">{children.length} alt bölüm</div>}
        </div>
        {hasChildren && <ul className={ulCls}>{children.map(renderNode)}</ul>}
      </li>
    );
  };
  const roots = tree.get(null) || [];
  return (
    <>
      <style>{`
        .wc-tree { display: inline-flex; padding-top: 0; }
        .wc-tree ul { display: flex; position: relative; margin: 0; list-style: none; padding-left: 0; padding-top: 0; }
        .wc-tree ul.wc-children { padding-top: 28px; }
        .wc-tree li { list-style: none; text-align: center; padding: 0 12px; position: relative; }
        .wc-tree ul.wc-children > li { padding-top: 28px; }
        .wc-tree ul.wc-children > li::before,
        .wc-tree ul.wc-children > li::after {
          content: ""; position: absolute; top: 0; height: 28px;
          border-top: 2px solid #94a3b8;
        }
        .wc-tree ul.wc-children > li::before { right: 50%; left: 0; }
        .wc-tree ul.wc-children > li::after  { left: 50%; right: 0; }
        .wc-tree ul.wc-children > li:only-child::before,
        .wc-tree ul.wc-children > li:only-child::after { display: none; }
        .wc-tree ul.wc-children > li:first-child::before,
        .wc-tree ul.wc-children > li:last-child::after { border: 0 none; }
        .wc-tree ul.wc-children > li:last-child::before { border-right: 2px solid #94a3b8; border-radius: 0 6px 0 0; }
        .wc-tree ul.wc-children > li:first-child::after { border-left:  2px solid #94a3b8; border-radius: 6px 0 0 0; }
        .wc-tree ul.wc-children::before {
          content: ""; position: absolute; top: 0; left: 50%;
          width: 0; height: 28px; border-left: 2px solid #94a3b8;
        }
        /* DİKEY YAPRAK LAYOUT — çocuklar iki sütun (grid) halinde dizilir.
           Parent'tan dikey trunk çıkar, iki yana ayrılır; her sütunun tepesine
           bir bağlantı gider. Sütun içindeki elemanlar alt alta stack olur. */
        .wc-tree ul.wc-children-vertical {
          display: grid !important;
          grid-template-columns: 1fr 1fr;
          column-gap: 64px; row-gap: 12px;
          padding: 44px 0 0 0;
          justify-items: center;
        }
        /* Base horizontal T-connectors'ı vertical layoutta iptal et */
        .wc-tree ul.wc-children-vertical > li::before,
        .wc-tree ul.wc-children-vertical > li::after {
          content: none !important;
        }
        /* Dikey trunk (parent'ın altından iniyor) */
        .wc-tree ul.wc-children-vertical::before {
          content: ""; position: absolute;
          top: 0; left: 50%;
          width: 0; height: 22px;
          border-left: 2px solid #94a3b8;
        }
        /* Ortadaki yatay ayırıcı — sol yarıdan sağ yarıya, iki sütunun
           merkezini birleştirir (grid 2 sütunun ortaları 25% ve 75%). */
        .wc-tree ul.wc-children-vertical::after {
          content: ""; position: absolute;
          top: 22px; left: 25%; right: 25%;
          height: 0;
          border-top: 2px solid #94a3b8;
        }
        .wc-tree ul.wc-children-vertical > li {
          padding: 0; position: relative;
          text-align: center;
        }
        /* Her sütunun ilk yaprağı: üstünden yatay ayırıcıya bir dikey çizgi */
        .wc-tree ul.wc-children-vertical > li:nth-child(-n+2) {
          position: relative;
        }
        .wc-tree ul.wc-children-vertical > li:nth-child(-n+2)::before {
          content: "" !important;
          position: absolute;
          left: 50%; top: -22px;
          width: 0; height: 22px;
          border-left: 2px solid #94a3b8;
          border-radius: 0;
          background: none;
        }
        /* Tek çocuklu durumda ayırıcıyı gizle — trunk direkt tek çocuğa gider */
        .wc-tree ul.wc-children-vertical:has(> li:only-child)::after { display: none; }
        .wc-tree ul.wc-children-vertical:has(> li:only-child) {
          grid-template-columns: 1fr;
        }
        .wc-box {
          display: inline-flex; flex-direction: column; align-items: center;
          min-width: 140px; max-width: 220px;
          padding: 8px 12px 10px; background: #fff;
          border: 1px solid #e2e8f0; border-top: 4px solid #475569;
          border-radius: 4px; box-shadow: 0 1px 2px rgba(15,23,42,0.05);
        }
        .wc-box-root {
          min-width: 220px; max-width: 340px; padding: 10px 16px 12px;
          border: 1.5px solid #1B4776; border-top-width: 6px;
          background: linear-gradient(180deg, #fff 0%, #F1F5F9 100%);
        }
        .wc-box-root .wc-name { font-size: 14px; }
        .wc-box:hover { box-shadow: 0 4px 10px rgba(15,23,42,0.10); transform: translateY(-1px); transition: all 120ms ease; }
        .wc-cat { font-size: 8px; letter-spacing: 0.18em; text-transform: uppercase; font-weight: 800; color: #fff; padding: 2px 8px; border-radius: 2px; margin-bottom: 6px; white-space: nowrap; }
        .wc-name { font-size: 12px; font-weight: 700; color: #0f172a; line-height: 1.15; word-wrap: break-word; }
        .wc-count { font-size: 9px; font-weight: 700; color: #64748b; margin-top: 4px; text-transform: uppercase; letter-spacing: 0.1em; }
      `}</style>
      {/* Zoom toolbar */}
      <div className="flex items-center gap-1.5 mb-3 sticky top-0 z-10 bg-white/95 backdrop-blur py-2 border-b border-slate-200"
           data-testid="wbs-zoom-toolbar">
        <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mr-2">
          Yakınlaştır
        </span>
        {ZOOM_STEPS.map((z) => (
          <button
            key={z}
            type="button"
            onClick={() => setZoom(z)}
            data-testid={`wbs-zoom-${Math.round(z * 100)}`}
            className={`px-2.5 py-1 text-[11px] font-bold border transition-colors ${
              Math.abs(zoom - z) < 0.001
                ? "bg-[#1B4776] text-white border-[#1B4776]"
                : "bg-white text-[#1B4776] border-slate-300 hover:border-[#1B4776]"
            }`}
          >
            %{Math.round(z * 100)}
          </button>
        ))}
        <span className="text-[10px] text-slate-400 ml-2">
          İpucu: CTRL + fare tekerleği ile de yakınlaştırabilirsiniz
        </span>
      </div>
      <div
        className="wc-tree"
        style={{ transform: `scale(${zoom})`, transformOrigin: "top left" }}
      >
        <ul>
          <li className="wc-node" data-testid="wbs-chart-node-project">
            <div className="wc-box wc-box-root" style={{ borderTopColor: "#1B4776" }}>
              <div className="wc-cat" style={{ backgroundColor: "#1B4776" }}>Proje</div>
              <div className="wc-name" title={projectLabel}>{projectLabel}</div>
              {projectCode && <div className="wc-count" style={{ color: "#95C11F" }}>{projectCode}</div>}
            </div>
            {roots.length > 0 && (
              <ul className="wc-children">{roots.map(renderNode)}</ul>
            )}
          </li>
        </ul>
      </div>
    </>
  );
}
